import React, { useCallback } from "react";
import {
  Edit,
  SimpleForm,
  BooleanInput,
  useNotify,
  useRefresh,
  TextField,
  ArrayField,
  Datagrid,
  Button,
  NotificationType,
  SelectInput,
  required,
  SaveButton,
  Toolbar,
  useRecordContext,
  ReferenceInput,
  AutocompleteInput,
} from "react-admin";
import { Box, Typography } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import {
  assignCountryToPlan,
  removeCountryFromPlan,
} from "../../../data/plans";

interface Country {
  id: string;
  isoCode: string;
  name: string;
}

interface Plan {
  id: string;
  countries: Country[];
}

const RemoveCountryButton = ({ planId }: { planId: string }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const country = useRecordContext<Country>();

  const handleRemoveCountry = useCallback(async () => {
    if (!country?.isoCode || !planId) {
      console.error("Missing required data:", { country, planId });
      return;
    }

    try {
      await removeCountryFromPlan(planId, country.isoCode);
      notify("Country removed successfully");
      refresh();
    } catch (error) {
      console.error("Error removing country:", error);
      notify("Error removing country", "error" as NotificationType);
    }
  }, [planId, country, notify, refresh]);

  if (!country) {
    return null;
  }

  return (
    <Button label="Remove" onClick={handleRemoveCountry} color="secondary">
      <DeleteIcon />
    </Button>
  );
};

const CountryList = () => {
  const record = useRecordContext<Plan>();

  if (!record?.countries?.length) {
    return <Typography>No countries assigned</Typography>;
  }

  return (
    <ArrayField source="countries">
      <Datagrid>
        <TextField source="isoCode" />
        <TextField source="name" />
        <RemoveCountryButton planId={record.id} />
      </Datagrid>
    </ArrayField>
  );
};

const AddCountry = () => {
  const [selectedCountry, setSelectedCountry] = React.useState<string>("");
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext<Plan>();

  const handleAddCountry = useCallback(async () => {
    if (!record?.id || !selectedCountry) {
      console.error("Missing required data:", { record, selectedCountry });
      return;
    }

    try {
      await assignCountryToPlan(record.id, selectedCountry);
      notify("Country added successfully");
      setSelectedCountry("");
      refresh();
    } catch (error) {
      console.error("Error adding country:", error);
      notify("Error adding country", "error" as NotificationType);
    }
  }, [record, selectedCountry, notify, refresh]);

  if (!record) {
    return null;
  }

  const filterToQuery = (searchText: string) => ({
    isoCode: searchText,
    name: searchText,
  });

  return (
    <Box
      display="flex"
      alignItems="center"
      style={{ marginTop: 16 }}
      gridGap={8}
    >
      <ReferenceInput
        label="Country"
        source="countryId"
        reference="countries"
        filterToQuery={filterToQuery}
        style={{ flex: 1, marginRight: 16 }}
      >
        <AutocompleteInput
          optionText={(record: any) => `${record.name} (${record.isoCode})`}
          fullWidth
          onSelect={(value: any) => {
            setSelectedCountry(value?.isoCode);
          }}
        />
      </ReferenceInput>
      <Button
        label="Add Country"
        onClick={handleAddCountry}
        disabled={!selectedCountry}
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
      />
    </Box>
  );
};

const PlanEditToolbar = (props: any) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

const transform = (data: any) => ({
  accessType: data.accessType,
  isDisplayed: data.isDisplayed,
});

export const PlanEdit = (props: any) => {
  return (
    <Edit {...props} actions={false} mutationMode="pessimistic">
      <SimpleForm toolbar={<PlanEditToolbar />} transform={transform}>
        <Typography variant="h6">Plan Details</Typography>
        <TextField source="id" label="Database ID" />
        <TextField source="name" />
        <TextField source="provider.name" />
        <TextField source="provider.id" />
        <TextField source="priceInCents" />
        <SelectInput
          source="accessType"
          choices={[
            { id: "basic", name: "Basic" },
            { id: "standard", name: "Standard" },
            { id: "premium", name: "Premium" },
            { id: "free", name: "Free" },
          ]}
          validate={[required()]}
        />
        <BooleanInput source="isDisplayed" />

        <Typography variant="h6" style={{ marginTop: "2rem" }}>
          Countries
        </Typography>
        <CountryList />
        <AddCountry />
      </SimpleForm>
    </Edit>
  );
};
